<template>
  <v-container class="pt-7 pb-11">
    <h1 class="text-h4 mb-5">{{ 'tagManagement'|localize }}</h1>
    <div class="white elevation-2 rounded">
      <v-row class="ma-0 mb-1">
        <v-col cols="12" sm="5" md="4">
          <v-text-field
            :background-color="search ? 'serenade' : ''"
            v-model.trim="search"
            prepend-inner-icon="mdi-magnify"
            outlined
            hide-details
            dense
            clearable
            :placeholder="'search'|localize"
          />
        </v-col>
        <v-spacer/>
      </v-row>
      <v-data-table
        :headers="headers"
        :items="filteredTags"
        :loading="loading"
        loader-height="2"
        :items-per-page="-1"
        item-key="id"
        hide-default-footer
      >
        <template v-slot:no-data>
          <EmptyState icon="mdi-inbox-outline" :title="'emptyGenericTitle'|localize" :subtitle="'emptyGenericSubtitle'|localize"/>
        </template>
        <template v-slot:item.name="{ item }">
          <span>{{ item.name }}</span>
        </template>
        <template v-slot:item.requests_count="{ item }">
          <v-chip small color="serenade">{{ item.requests_count || 0 }}</v-chip>
        </template>
        <template v-slot:item.settings="{ item }">
          <v-progress-circular
            v-if="isRowBusy(item)"
            indeterminate
            :width="1"
            :size="20"
          />
          <v-menu v-else left transition="slide-y-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-avatar v-bind="attrs" v-on="on" size="45" tile>
                <v-icon size="20">mdi-dots-vertical</v-icon>
              </v-avatar>
            </template>
            <v-list>
              <v-list-item link @click="openUpdateTag(item)">
                <v-list-item-title>{{ 'update'|localize }}</v-list-item-title>
              </v-list-item>
              <v-list-item link @click="confirmDelete(item)">
                <v-list-item-title>{{ 'delete'|localize }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </div>
    <v-dialog v-model="updateDialog" width="420" overlay-color="black" :overlay-opacity="0.3" @click:outside="closeUpdateDialog">
      <v-card class="px-6">
        <v-card-title class="text-h5 d-block pt-6 pb-4 px-0" style="word-break: normal;">{{ 'updateTagTitle'|localize }}</v-card-title>
        <v-card-text class="px-0 pb-0">
          <v-text-field
            v-model.trim="tagNameInput"
            :label="'name'|localize"
            outlined
            dense
            autofocus
            hide-details
            @keyup.enter="updateSelectedTag"
          />
        </v-card-text>
        <v-card-actions class="py-6 px-0">
          <v-row>
            <v-col>
              <v-btn color="primary" block outlined rounded @click="closeUpdateDialog">{{ 'cancel'|localize }}</v-btn>
            </v-col>
            <v-col>
              <v-btn
                color="primary"
                block
                depressed
                rounded
                :loading="savingId === selectedTagId"
                :disabled="!canSaveSelectedTag"
                @click="updateSelectedTag"
              >
                {{ 'update'|localize }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <SimpleDialog
      ref="deleteTagDialog"
      :title="'deleteTagTitle'|localize"
      :text="'deleteTagText'|localize"
      :accept-text="'delete'|localize"
      :decline-text="'cancel'|localize"
      @accept="deleteSelectedTag"
    />
  </v-container>
</template>

<script>
  import api from '@/api/api';
  import localize from '@/filters/localize';
  import methods from '@/helpers/methods';
  import SimpleDialog from '@/components/common/SimpleDialog';
  import EmptyState from '@/components/common/EmptyState';

  export default {
    name: 'AdminTags',

    components: {SimpleDialog, EmptyState},

    data() {
      return {
        loading: true,
        savingId: null,
        deletingId: null,
        search: '',
        tags: [],
        selectedTag: null,
        updateDialog: false,
        tagNameInput: '',
      };
    },

    mounted() {
      this.getTags();
    },

    computed: {
      headers() {
        return [
          {text: localize('name'), value: 'name', sortable: true},
          {text: localize('requests'), value: 'requests_count', sortable: true, width: 140, align: 'center'},
          {text: '', value: 'settings', sortable: false, width: 80, align: 'center'},
        ];
      },
      filteredTags() {
        if (!this.search) return this.tags;
        let search = this.search.toLowerCase();
        return this.tags.filter((tag) => tag.name.toLowerCase().includes(search));
      },
      selectedTagId() {
        return this.selectedTag ? this.selectedTag.id : null;
      },
      canSaveSelectedTag() {
        let name = this.tagName(this.tagNameInput);
        return !!this.selectedTag && !!name && name !== this.selectedTag.name;
      },
    },

    methods: {
      normalizeTag(tag) {
        return {
          ...tag,
          requests_count: tag.requests_count || 0,
        };
      },
      tagName(value) {
        return typeof value === 'string' ? value.trim() : '';
      },
      isRowBusy(item) {
        return this.savingId === item.id || this.deletingId === item.id;
      },
      async getTags() {
        try {
          this.loading = true;
          let response = await api.getTags();
          this.tags = (response.data || []).map(this.normalizeTag);
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.loading = false;
        }
      },
      openUpdateTag(item) {
        this.selectedTag = item;
        this.tagNameInput = item.name;
        this.updateDialog = true;
      },
      closeUpdateDialog() {
        this.updateDialog = false;
        this.selectedTag = null;
        this.tagNameInput = '';
      },
      async updateSelectedTag() {
        if (!this.canSaveSelectedTag) return;

        let name = this.tagName(this.tagNameInput);

        try {
          this.savingId = this.selectedTag.id;
          let response = await api.updateTag(this.selectedTag.id, {name});
          Object.assign(this.selectedTag, this.normalizeTag(response.data));
          this.closeUpdateDialog();
          methods.showGlobalSnackbar('success', localize('done'));
          // Keep the shared tags list (AdminRequests' "Filters by tags") in sync with the rename.
          this.$store.dispatch('loadTags').catch(() => {});
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.savingId = null;
        }
      },
      confirmDelete(item) {
        this.selectedTag = item;
        this.$refs.deleteTagDialog.open();
      },
      async deleteSelectedTag() {
        if (!this.selectedTag) return;

        try {
          this.deletingId = this.selectedTag.id;
          await api.deleteTag(this.selectedTag.id);
          this.tags = this.tags.filter((tag) => tag.id !== this.selectedTag.id);
          this.selectedTag = null;
          methods.showGlobalSnackbar('success', localize('done'));
          // Keep the shared tags list (AdminRequests' "Filters by tags") in sync with the deletion.
          this.$store.dispatch('loadTags').catch(() => {});
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.deletingId = null;
        }
      },
    }
  };
</script>
